import { AdminCreds, MessageInfo, UserInfo } from "../types";
import { baseFetch } from "./base";
import { AxiosError } from "axios";
import { normalizeDomainTree } from "../utils/domainFocus";

export const validateAdminCredentials = async ({
  adminLogin,
  adminPassword,
}: AdminCreds): Promise<{
  valid: boolean;
  message?: string;
}> => {
  try {
    const response = await baseFetch.get("/admin/verify", {
      headers: {
        "admin-login": adminLogin,
        "admin-password": adminPassword,
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error validating admin credentials:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const getUsers = async ({
  adminLogin,
  adminPassword,
}: AdminCreds): Promise<any> => {
  try {
    const response = await baseFetch.get("/admin/get_users", {
      headers: {
        "admin-login": adminLogin,
        "admin-password": adminPassword,
      },
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error getting users:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const uploadInfoImage = async (
  formData: FormData,
  { adminLogin, adminPassword }: AdminCreds
) => {
  try {
    const response = await baseFetch.post(
      "/admin/upload_info_image",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "admin-login": adminLogin,
          "admin-password": adminPassword,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error uploading info image:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const addInfoMessage = async (
  messageInfo: MessageInfo,
  { adminLogin, adminPassword }: AdminCreds
) => {
  try {
    const response = await baseFetch.post(
      "/admin/add_info_message",
      messageInfo,
      {
        headers: {
          "admin-login": adminLogin,
          "admin-password": adminPassword,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error adding info message:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const previewInfoMesssage = async (
  messageInfo: MessageInfo,
  { adminLogin, adminPassword }: AdminCreds
) => {
  try {
    const response = await baseFetch.post(
      "/admin/info_message_preview",
      messageInfo,
      {
        headers: {
          "admin-login": adminLogin,
          "admin-password": adminPassword,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error previewing info message:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const getAdminDomainTree = async ({
  adminLogin,
  adminPassword,
}: AdminCreds) => {
  try {
    const response = await baseFetch.get("/admin/get_domain_tree", {
      headers: {
        "admin-login": adminLogin,
        "admin-password": adminPassword,
      },
    });

    return normalizeDomainTree(response.data);
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error getting admin domain tree:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const getAdminCompanyStructure = async ({
  adminLogin,
  adminPassword,
}: AdminCreds) => {
  try {
    const response = await baseFetch.get("/admin/get_company_structure", {
      headers: {
        "admin-login": adminLogin,
        "admin-password": adminPassword,
      },
    });

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error(
        "Axios error getting admin company structure:",
        error.message
      );

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const createUser = async (
  userInfo: UserInfo,
  { adminLogin, adminPassword }: AdminCreds
) => {
  try {
    const response = await baseFetch.post("/admin/create_user", userInfo, {
      headers: {
        "admin-login": adminLogin,
        "admin-password": adminPassword,
      },
    });

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error creating user:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const uploadUserCsv = async (
  formData: FormData,
  { adminLogin, adminPassword }: AdminCreds
) => {
  try {
    const response = await baseFetch.post("/admin/upload_users_csv", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "admin-login": adminLogin,
        "admin-password": adminPassword,
      },
    });

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error uploading user's csv:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const deleteUsers = async (
  selectedLogins: string[],
  { adminLogin, adminPassword }: AdminCreds
) => {
  try {
    const response = await baseFetch.post(
      "/admin/delete_user",
      {
        logins_to_delete: selectedLogins,
      },
      {
        headers: {
          "admin-login": adminLogin,
          "admin-password": adminPassword,
        },
      }
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error deleting users:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const addAssessment = async (
  assessment: Record<string, unknown>,
  { adminLogin, adminPassword }: AdminCreds
) => {
  try {
    const response = await baseFetch.post("/assessment/add", assessment, {
      headers: {
        "admin-login": adminLogin,
        "admin-password": adminPassword,
      },
    });

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error adding assessment:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export const deleteAssessment = async (
  assessmentId: string,
  { adminLogin, adminPassword }: AdminCreds
) => {
  try {
    const response = await baseFetch.delete("/assessment", {
      params: { assessment_id: assessmentId },
      headers: {
        "admin-login": adminLogin,
        "admin-password": adminPassword,
      },
    });

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error deleting assessment:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};

export interface ActualAssessmentListItem {
  id: string;
  title: string;
  focus?: string;
  domain?: string;
  time_to_send?: string;
  answers_time_limit?: number;
  active_time?: number;
  /** Full assessment payload from actual_list (for edit/re-upload). */
  raw: Record<string, unknown>;
}

const normalizeActualAssessmentItem = (
  value: unknown
): ActualAssessmentListItem | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const id =
    typeof raw.id === "string" && raw.id.trim()
      ? raw.id.trim()
      : typeof raw.assessment_id === "string" && raw.assessment_id.trim()
        ? raw.assessment_id.trim()
        : "";
  if (!id) return null;

  return {
    id,
    title:
      typeof raw.title === "string" && raw.title.trim()
        ? raw.title.trim()
        : id,
    focus: typeof raw.focus === "string" ? raw.focus : undefined,
    domain: typeof raw.domain === "string" ? raw.domain : undefined,
    time_to_send:
      typeof raw.time_to_send === "string" ? raw.time_to_send : undefined,
    answers_time_limit:
      typeof raw.answers_time_limit === "number"
        ? raw.answers_time_limit
        : undefined,
    active_time:
      typeof raw.active_time === "number" ? raw.active_time : undefined,
    raw,
  };
};

export const getActualAssessmentList = async ({
  adminLogin,
  adminPassword,
}: AdminCreds): Promise<ActualAssessmentListItem[]> => {
  try {
    const response = await baseFetch.get("/assessment/actual_list", {
      headers: {
        "admin-login": adminLogin,
        "admin-password": adminPassword,
      },
    });

    const data: unknown = response.data;
    const rawList: unknown[] = Array.isArray(data)
      ? data
      : data &&
          typeof data === "object" &&
          Array.isArray((data as { actual_list?: unknown }).actual_list)
        ? ((data as { actual_list: unknown[] }).actual_list)
        : data &&
            typeof data === "object" &&
            Array.isArray((data as { assessments?: unknown }).assessments)
          ? ((data as { assessments: unknown[] }).assessments)
          : [];

    return rawList
      .map(normalizeActualAssessmentItem)
      .filter((item): item is ActualAssessmentListItem => item !== null);
  } catch (error) {
    if (error instanceof AxiosError) {
      console.error("Axios error fetching actual assessments:", error.message);

      if (error.response?.status === 401) {
        console.warn("Unauthorized access. You may need to refresh the token.");
      }
    } else {
      console.error("Unknown error:", error);
    }
    throw error;
  }
};
